import React, { useEffect, useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { hashHistory } from 'react-router'
import {
  previewCollectionsTrigger,
  runCollectionsTrigger
} from '../../lib/requests.es6'
import {
  buildAmountRule,
  channelLabel,
  coverageNote,
  errorMessage,
  formatDate,
  formatMoney,
  skipLabel,
  validateAmountRule
} from '../../lib/collections.es6'
import { Loading } from './shared.es6'

/**
 * "Collect now" for one or many debit orders. It never sends anything on the first click: it shows exactly
 * what would be attempted (and what would be skipped and why) and only starts when the user confirms with
 * a reason. The server re-checks holds and in-flight attempts when it dispatches.
 *
 * props: open, onClose, selection (API selection object), subject (text shown in the title), onDone(run),
 * agentHold (the order is on an agent-only hold: automatic collection is off, an agent collects once the customer agrees)
 */
export default function TriggerDialog({
  open,
  onClose,
  selection: liveSelection,
  subject: liveSubject,
  onDone,
  agentHold
}) {
  // What you previewed is what you collect: the selection is fixed when the dialog opens. The page behind it may
  // clear or reload its own selection (it does after a successful run) without changing or emptying this one.
  const frozen = useRef(null)
  if (!open) frozen.current = null
  else if (!frozen.current)
    frozen.current = { selection: liveSelection, subject: liveSubject }
  const selection = open ? frozen.current.selection : liveSelection
  const subject = open ? frozen.current.subject : liveSubject

  const [mode, setMode] = useState('full')
  const [value, setValue] = useState('')
  const [reason, setReason] = useState('')
  // An agent-only hold is bypassed only when the agent says the customer agreed to be debited now.
  const [agreed, setAgreed] = useState(false)
  const overrideAgentHolds = !!agentHold && agreed
  const [preview, setPreview] = useState({
    loading: false,
    data: null,
    error: null
  })
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState(null)
  const [submitError, setSubmitError] = useState(null)

  const ruleError = validateAmountRule(mode, value)
  const selectionKey = JSON.stringify(selection || {})

  useEffect(() => {
    if (!open) return undefined
    setResult(null)
    setSubmitError(null)
    if (ruleError) {
      setPreview({ loading: false, data: null, error: null })
      return undefined
    }
    let cancelled = false
    setPreview({ loading: true, data: null, error: null })
    previewCollectionsTrigger({
      selection,
      amount: buildAmountRule(mode, value),
      max_items: 8,
      ...(overrideAgentHolds ? { override_agent_holds: true } : {})
    }).then(
      response => {
        if (!cancelled)
          setPreview({ loading: false, data: response.data, error: null })
      },
      error => {
        if (!cancelled) setPreview({ loading: false, data: null, error })
      }
    )
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectionKey, mode, value, overrideAgentHolds])

  const data = preview.data
  const canStart =
    !!data &&
    data.will_attempt > 0 &&
    !ruleError &&
    reason.trim().length >= 3 &&
    !submitting

  const start = () => {
    setSubmitting(true)
    setSubmitError(null)
    runCollectionsTrigger({
      selection,
      amount: buildAmountRule(mode, value),
      reason: reason.trim(),
      confirm: true,
      ...(overrideAgentHolds ? { override_agent_holds: true } : {})
    }).then(
      response => {
        setSubmitting(false)
        setResult(response.data)
        if (onDone) onDone(response.data)
      },
      error => {
        setSubmitting(false)
        setSubmitError(error)
      }
    )
  }

  const close = () => {
    setMode('full')
    setValue('')
    setReason('')
    setAgreed(false)
    setResult(null)
    setPreview({ loading: false, data: null, error: null })
    onClose()
  }

  return (
    <Dialog
      open={!!open}
      onClose={submitting ? undefined : close}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Collect now{subject ? `: ${subject}` : ''}</DialogTitle>
      <DialogContent dividers>
        {result ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              {result.queued_count} attempt
              {result.queued_count === 1 ? '' : 's'} started
              {result.skipped_count ? `, ${result.skipped_count} skipped` : ''}.
            </Alert>
            <Typography variant="body2">
              Results appear in the attempt history as the partner answers,
              usually within a minute or two.
            </Typography>
            <Button
              sx={{ mt: 1 }}
              size="small"
              onClick={() => {
                close()
                hashHistory.push(
                  `/admin/collections/attempts?run=${result.guid}`
                )
              }}
            >
              View this run's attempts
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {agentHold ? (
              <Alert severity="warning">
                This debit order is on an agent-only hold, so nothing is
                collected automatically. Collect only after the customer has
                agreed to be debited now.
                <FormControlLabel
                  sx={{ display: 'block', mt: 1 }}
                  control={
                    <Checkbox
                      checked={agreed}
                      onChange={e => setAgreed(e.target.checked)}
                    />
                  }
                  label="The customer has agreed to be debited now"
                />
              </Alert>
            ) : null}
            <FormControl>
              <Typography variant="subtitle2">Amount to collect</Typography>
              <RadioGroup
                row
                value={mode}
                onChange={e => setMode(e.target.value)}
              >
                <FormControlLabel
                  value="full"
                  control={<Radio size="small" />}
                  label="Full amount"
                />
                <FormControlLabel
                  value="percent"
                  control={<Radio size="small" />}
                  label="A percentage"
                />
                <FormControlLabel
                  value="instalments"
                  control={<Radio size="small" />}
                  label="Several instalments"
                />
                <FormControlLabel
                  value="fixed"
                  control={<Radio size="small" />}
                  label="A fixed amount"
                />
              </RadioGroup>
              {mode !== 'full' ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap'
                  }}
                >
                  <TextField
                    size="small"
                    type="number"
                    label={
                      mode === 'percent'
                        ? 'Percent of one instalment (1-100)'
                        : mode === 'instalments'
                        ? 'Number of instalments'
                        : 'Amount (GHS)'
                    }
                    value={value}
                    onChange={e => setValue(e.target.value)}
                    error={!!ruleError}
                    helperText={
                      ruleError ||
                      (mode === 'fixed'
                        ? 'Any amount. For a customer who is behind it can be more than one instalment.'
                        : mode === 'instalments'
                        ? 'For example 2 to collect two months at once.'
                        : ' ')
                    }
                    inputProps={{
                      min: 0,
                      step:
                        mode === 'percent'
                          ? 1
                          : mode === 'instalments'
                          ? 1
                          : 0.01
                    }}
                    sx={{ maxWidth: 280 }}
                  />
                  {mode === 'instalments'
                    ? [1, 2, 3].map(n => (
                        <Button
                          key={n}
                          size="small"
                          variant={
                            String(value) === String(n)
                              ? 'contained'
                              : 'outlined'
                          }
                          onClick={() => setValue(String(n))}
                        >
                          {n}
                        </Button>
                      ))
                    : null}
                </Box>
              ) : null}
            </FormControl>

            {preview.loading ? <Loading /> : null}
            {preview.error ? (
              <Alert severity="error">{errorMessage(preview.error)}</Alert>
            ) : null}
            {data ? (
              <Box>
                <Alert
                  severity={data.will_attempt > 0 ? 'info' : 'warning'}
                  sx={{ mb: 1 }}
                >
                  {data.will_attempt > 0
                    ? `${data.will_attempt} of ${
                        data.total
                      } will be attempted, ${formatMoney(
                        data.attempt_amount_in_cents
                      )} in total.`
                    : `Nothing can be attempted: all ${data.total} would be skipped.`}
                  {data.skipped > 0 && data.will_attempt > 0
                    ? ` ${data.skipped} will be skipped.`
                    : ''}
                </Alert>
                {data.items
                  .filter(item => coverageNote(item))
                  .map(item => {
                    const note = coverageNote(item)
                    return (
                      <Alert
                        key={`note-${item.debit_order_guid}`}
                        severity={note.severity}
                        sx={{ mb: 1 }}
                      >
                        {note.text}
                      </Alert>
                    )
                  })}
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer</TableCell>
                      <TableCell>Channel</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Outcome</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.items.map(item => (
                      <TableRow key={item.debit_order_guid}>
                        <TableCell>{item.msisdn}</TableCell>
                        <TableCell>{channelLabel(item.channel)}</TableCell>
                        <TableCell align="right">
                          {formatMoney(item.attempt_amount_in_cents)}
                        </TableCell>
                        <TableCell>
                          {item.will_attempt
                            ? 'Will attempt'
                            : `${skipLabel(item.skip_reason)}${
                                item.hold_reason ? `: ${item.hold_reason}` : ''
                              }${
                                item.paid_until
                                  ? ` until ${formatDate(item.paid_until)}`
                                  : ''
                              }`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {data.total > data.items.length ? (
                  <Typography variant="caption" color="text.secondary">
                    Showing {data.items.length} of {data.total}.
                  </Typography>
                ) : null}
              </Box>
            ) : null}

            <TextField
              size="small"
              label="Reason (kept in the audit log)"
              value={reason}
              onChange={e => setReason(e.target.value)}
              helperText="For example: customer called to say funds are available"
              multiline
              minRows={2}
            />
            {submitError ? (
              <Alert severity="error">{errorMessage(submitError)}</Alert>
            ) : null}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={submitting}>
          {result ? 'Close' : 'Cancel'}
        </Button>
        {!result ? (
          <Button variant="contained" onClick={start} disabled={!canStart}>
            {submitting
              ? 'Starting...'
              : data
              ? `Collect ${data.will_attempt}`
              : 'Collect'}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  )
}
